/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RoleEnum } from "@/enum/role";
import { StatusEnumStoreConciliation } from "@/enum/statusConciliation";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { getUserFromLocalStorage } from "@/utils/getUserFromLocalStorage";
import { formatToBRL, formatToMillionBRL } from "@/utils/formatToMoney";
import { ExtendedColumnDef } from "components/Datatable";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import { EyeIcon, LockOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { OpenConciliation } from "../conciliation/openConciliation";
import SortedCell from "./components/sorted-cell";

interface CellAction {
  text: string;
  action: () => void;
}

export function getCellActionByStatus(store: any) {
  const user = getUserFromLocalStorage();
  const { handleModal } = useDialogModal();

  const moduleAccess = localStorage.getItem("@verzel:module");

  const group = user?.user_permission_group?.find(
    (item: any) => item.group === moduleAccess
  );

  const openConciliation = () => {
    handleModal({
      isOpen: true,
      element: <OpenConciliation conciliation_id={store.id} />,
      title: "Abrir Conciliação",
    });
  };

  const navigate = useNavigate();
  const path = `/conciliacao/lojas/conciliacao-de-caixa-de-loja/${store.id}`;
  const cell: { [key in any]: CellAction } = {
    [StatusEnumStoreConciliation.OPENED]: {
      text: "Conciliar",
      action: () => navigate(path, { state: { from: location.pathname } }),
    },
    [StatusEnumStoreConciliation.NOT_INTEGRATED]: {
      text: "",
      action: () => void 0,
    },
    [StatusEnumStoreConciliation.CLOSED]: group?.role.includes(
      RoleEnum.ADMINISTRADOR
    )
      ? {
          text: "Reabrir",
          action: () => {
            openConciliation();
          },
        }
      : {
          text: "Visualizar",
          action: () => {
            navigate(path, { state: { from: location.pathname } });
          },
        },
    [StatusEnumStoreConciliation.PENDING]: {
      text: "Conciliar",
      action: () => navigate(path, { state: { from: location.pathname } }),
    },
  };

  return store.status === StatusEnumStoreConciliation.CLOSED &&
    group?.role.includes(RoleEnum.ADMINISTRADOR) ? (
    <DropdownMenu>
      <DropdownMenuTrigger>Ações</DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>Ações de Conciliação</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer flex items-center justify-center gap-2"
          onClick={() => cell[store.status].action()}
        >
          <LockOpen className="text-black" size={13} /> Reabrir Conciliação
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer flex items-center justify-center gap-2"
          onClick={() => navigate(path, { state: { from: location.pathname } })}
        >
          <EyeIcon className="text-black" size={13} /> Visualizar Conciliação
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : (
    <button onClick={() => cell[store.status].action()}>
      {cell[store.status].text}{" "}
    </button>
  );
}

export function handleCellStyleByStatus(status: string) {
  const color: any = {
    [StatusEnumStoreConciliation.OPENED]: { label: "Aberto", color: "#00AC4F" },
    [StatusEnumStoreConciliation.CLOSED]: {
      label: "Fechado",
      color: "#B3B3B3",
    },
    [StatusEnumStoreConciliation.NOT_INTEGRATED]: {
      label: "Não integrado",
      color: "#79606C",
    },
    [StatusEnumStoreConciliation.PENDING]: {
      label: "Aguardando",
      color: "#E3B400",
    },
  };

  return (
    <div
      className="text-right"
      style={{ color: color[status]?.color ?? "#444444" }}
    >
      {color[status]?.label}
    </div>
  );
}

export const columns: ExtendedColumnDef<any>[] = [
  {
    accessorKey: "store.code",
    header: ({ column }) => (
      <SortedCell column={column} title={"Lojas"} orderName="code" />
    ),
    cell: ({ row }) => `Loja ${row?.original?.store?.code}`,
    className: "store-column",
  },
  {
    accessorKey: "store.responsible.name",
    header: "Responsável",
    className: "reponsible-column",
    cell: ({ row }) => `${row?.original?.store?.responsible?.name || ""}`,
  },
  {
    accessorKey: "store.regional.id",
    header: "Regional",
    cell: ({ row }) => `${row?.original?.store?.regional?.name || ""}`,
    className: "region-column",
  },
  {
    accessorKey: "returns_count",
    header: "Devoluções",
    cell: ({ row }) => (
      <div className="text-right">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="cursor-pointer">{row?.original?.returns_count}</p>
            </TooltipTrigger>
            <TooltipContent className="bg-[#141414] text-white text-center">
              <p>{row?.original?.returns_count}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
    className: "returns-column",
  },
  {
    accessorKey: "total_sales",
    header: "Total de Vendas",
    cell: ({ row }) => (
      <div className="text-right">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="cursor-pointer">
                {formatToMillionBRL(row?.original?.total_sales)}
              </p>
            </TooltipTrigger>
            <TooltipContent className="bg-[#141414] text-white text-center">
              <p>{formatToBRL(row?.original?.total_sales)}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
    className: "sells-column",
  },
  {
    accessorKey: "total_deposits",
    header: "Total Depósito",
    cell: ({ row }) => (
      <div className="text-right">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="cursor-pointer">
                {formatToMillionBRL(row?.original?.total_deposits)}
              </p>
            </TooltipTrigger>
            <TooltipContent className="bg-[#141414] text-white text-center">
              <p>{formatToBRL(row?.original?.total_deposits)}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
    className: "deposit-column",
  },
  {
    accessorKey: "total_divergences_sum",
    header: "Divergência",
    cell: ({ row }) => (
      <div className="text-right">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="cursor-pointer">
                {formatToBRL(row?.original?.total_divergences_sum)}
              </p>
            </TooltipTrigger>
            <TooltipContent className="bg-[#141414] text-white text-center">
              <p>{formatToBRL(row?.original?.total_divergences_sum)}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
    className: "divergence-column",
  },
  {
    accessorKey: "status",
    header: () => <div> Situação </div>,
    cell: ({ row }) => {
      return handleCellStyleByStatus(row.original?.status);
    },
    className: "status-column",
  },
  {
    id: "actions",
    header: () => <div> Ações </div>,
    cell: ({ row }) => {
      const store = row.original;

      return (
        <ActionCell
          noAction={
            store?.status === StatusEnumStoreConciliation.NOT_INTEGRATED
          }
        >
          {store?.status !== StatusEnumStoreConciliation.NOT_INTEGRATED &&
            getCellActionByStatus(store)}
        </ActionCell>
      );
    },
    className: "actions-column",
  },
];

const ActionCell = styled.div<{ noAction?: boolean }>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: end;
  padding: 0 10px;
  ${({ noAction }) => (noAction ? "background: #E9E9EF;" : "")}
  button {
    border: none;
    background: transparent;
    color: #5577b9;
    text-decoration: underline;
    width: fit-content;
    padding: 0;

    &:hover {
      filter: brightness(0.65);
    }
  }
`;
